import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle2, RotateCcw, Clock } from 'lucide-react';
import { toast } from 'sonner';

const formatDateFR = (dateStr) => {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return dateStr; }
};

const EngagementActive = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/engagements');
        const all = [
          ...(res.data.today || []),
          ...(res.data.upcoming || []),
          ...(res.data.history || []),
        ];
        const found = all.find((e) => e.id === id);
        setEng(found || null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, api]);

  const setStatus = async (statut) => {
    setSaving(true);
    try {
      await api.put(`/api/engagements/${id}/status`, { statut });
      toast.success(statut === 'en_cours' ? 'C\'est parti.' : 'Engagement reporté.');
      navigate('/dashboard');
    } catch {
      toast.error('Impossible de mettre à jour');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] bg-atlasBg flex items-center justify-center">
        <div className="text-atlasCyan animate-pulse">Chargement…</div>
      </div>
    );
  }

  if (!eng) {
    return (
      <div className="fixed inset-0 z-[60] bg-atlasBg flex flex-col items-center justify-center p-6 text-center">
        <p className="text-white font-medium mb-3">Engagement introuvable.</p>
        <Button onClick={() => navigate('/engagements')} className="btn-primary">
          Voir mes engagements
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="engagement-active-page" className="fixed inset-0 z-[60] bg-atlasBg flex flex-col">
      <div className="flex-shrink-0 px-6 pt-10 pb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-atlasCyan mb-2">Ton engagement maintenant</p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 flex flex-col items-center justify-center text-center">
        <p className="text-2xl sm:text-3xl font-semibold text-white leading-tight mb-6 max-w-xl">
          {eng.description}
        </p>
        <div className="inline-flex items-center gap-2 text-sm text-atlasTextSecondary">
          <Clock className="w-4 h-4 text-atlasCyan" />
          <span>
            {formatDateFR(eng.date_engagement)} · {eng.heure_debut}
            {eng.heure_fin ? ` – ${eng.heure_fin}` : ''}
          </span>
        </div>
      </div>

      <div
        className="flex-shrink-0 px-6 pb-6 pt-4 border-t border-atlasBorder bg-atlasBg/90 backdrop-blur-lg flex flex-col gap-3"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}
      >
        <Button
          data-testid="eng-active-go-btn"
          onClick={() => setStatus('en_cours')}
          disabled={saving}
          className="w-full btn-primary h-12 text-base"
        >
          <CheckCircle2 className="w-5 h-5 mr-2" /> OK, j'y vais
        </Button>
        <Button
          data-testid="eng-active-postpone-btn"
          variant="outline"
          onClick={() => setStatus('reporte')}
          disabled={saving}
          className="w-full h-12 text-base border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
        >
          <RotateCcw className="w-5 h-5 mr-2" /> Je dois reporter
        </Button>
      </div>
    </div>
  );
};

export default EngagementActive;
